// Parse an uploaded file (CSV / TSV / XLS / XLSX / ODS / JSON) into flat rows +
// inferred field types, ready to publish as a Data Studio table for charting.
// Multi-sheet workbooks return every sheet so the user can pick which to import.
//
// SheetJS is ~330 kB and is only reachable from an explicit upload, so it is
// imported on demand rather than bundled into the app shell. It is installed
// from the vendor's own distribution (see README) because the npm copy is
// pinned at 0.18.5, which carries two unfixed advisories.
import type * as XlsxModule from 'xlsx';

import type { DataSheetField } from './api';

type SheetJs = typeof XlsxModule;

let sheetJs: Promise<SheetJs> | null = null;

/** Load (once) the spreadsheet parser. */
function loadSheetJs(): Promise<SheetJs> {
  sheetJs ??= import('xlsx');
  return sheetJs;
}

/**
 * A parse is bounded before it starts: a hostile or accidental 200 MB workbook
 * would otherwise lock the tab up inside the parser with no way back.
 */
const MAX_FILE_BYTES = 25 * 1024 * 1024;

function assertParseable(file: File): void {
  if (file.size > MAX_FILE_BYTES) {
    throw new Error(
      `That file is ${(file.size / 1024 / 1024).toFixed(0)} MB. Files up to 25 MB can be imported — ` +
        'split it, or export a narrower range.',
    );
  }
}

export interface ParsedSheet {
  fields: DataSheetField[];
  rows: Array<Record<string, unknown>>;
}

// A workbook may hold several tables (Excel tabs); each becomes a candidate source.
export interface ParsedWorkbook {
  sheetNames: string[];
  sheets: Record<string, ParsedSheet>;
}

const DATE_RE = /^\d{4}-\d{2}(-\d{2})?([T ]\d{2}:\d{2}(:\d{2})?)?/;

function pad(n: number) { return String(n).padStart(2, '0'); }

// SheetJS (with cellDates:true) hands back a real Date for any cell IT recognizes
// as a date — whatever the spreadsheet's display format. We don't parse/guess
// date text ourselves (that would mis-handle locale order like D/M vs M/D). We
// just canonicalize that Date to an ISO string the backend reads as datetime.
//
// SheetJS builds the Date in the runtime's local TZ from the serial, with a small
// rounding error that can land a date-only cell a few seconds before midnight on
// the previous day. Rounding to the nearest minute and reading LOCAL parts (the
// same TZ SheetJS used) recovers the calendar date the sheet actually shows.
function normalizeValue(v: unknown): unknown {
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    const d = new Date(Math.round(v.getTime() / 60000) * 60000);
    const datePart = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const hasTime = d.getHours() || d.getMinutes();
    return hasTime ? `${datePart} ${pad(d.getHours())}:${pad(d.getMinutes())}:00` : datePart;
  }
  return v;
}

function inferType(values: unknown[]): DataSheetField['type'] {
  const seen = values.filter((v) => v !== null && v !== undefined && v !== '');
  if (!seen.length) return 'string';
  let allNum = true;
  let allBool = true;
  let allDate = true;
  for (const v of seen) {
    if (typeof v === 'boolean') { allNum = false; allDate = false; continue; }
    allBool = false;
    const s = String(v).trim();
    if (allNum && (s === '' || Number.isNaN(Number(s)))) allNum = false;
    if (allDate && !DATE_RE.test(s)) allDate = false;
  }
  if (allBool) return 'boolean';
  if (allNum) return 'number';
  if (allDate) return String(seen[0]).length > 10 ? 'datetime' : 'date';
  return 'string';
}

// Flatten one level of nested objects (a.b → a_b), normalize values, trim keys.
function flattenRow(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [rawKey, v] of Object.entries(obj)) {
    const k = String(rawKey).trim();
    if (v && typeof v === 'object' && !(v instanceof Date) && !Array.isArray(v)) {
      for (const [k2, v2] of Object.entries(v as Record<string, unknown>)) out[`${k}_${String(k2).trim()}`] = normalizeValue(v2);
    } else {
      out[k] = Array.isArray(v) ? JSON.stringify(v) : normalizeValue(v);
    }
  }
  return out;
}

function fieldsFromRows(rows: Array<Record<string, unknown>>): DataSheetField[] {
  const keys: string[] = [];
  for (const r of rows) for (const k of Object.keys(r)) if (!keys.includes(k)) keys.push(k);
  return keys.map((name) => ({ name, type: inferType(rows.map((r) => r[name])) }));
}

function rowsToSheet(rows: Array<Record<string, unknown>>): ParsedSheet {
  if (!rows.length) throw new Error('No rows found');
  const flat = rows.map(flattenRow);
  return { rows: flat, fields: fieldsFromRows(flat) };
}

function jsonToRows(parsed: unknown): Array<Record<string, unknown>> {
  const arr = Array.isArray(parsed)
    ? parsed
    : parsed && typeof parsed === 'object'
      ? ((Object.values(parsed as Record<string, unknown>).find((v) => Array.isArray(v)) as unknown[]) ?? [parsed])
      : [];
  return arr.filter((r) => r && typeof r === 'object') as Array<Record<string, unknown>>;
}

// Parse pasted text: a JSON array/object, or CSV/TSV with a header row.
export async function parseSheetText(text: string): Promise<ParsedSheet> {
  const trimmed = text.trim();
  if (!trimmed) throw new Error('Nothing to parse');
  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    return rowsToSheet(jsonToRows(JSON.parse(trimmed)));
  }
  const XLSX = await loadSheetJs();
  const wb = XLSX.read(trimmed, { type: 'string', cellDates: true });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: null, raw: true });
  return rowsToSheet(rows);
  // (numbers stay numeric, dates stay Date objects → canonicalized in flattenRow)
}

// Parse a file into one-or-more sheets. CSV/TSV/JSON yield a single sheet;
// XLS/XLSX/ODS workbooks yield every non-empty tab.
export async function parseWorkbookFile(file: File): Promise<ParsedWorkbook> {
  assertParseable(file);
  const name = file.name.toLowerCase();

  if (name.endsWith('.json')) {
    const rows = jsonToRows(JSON.parse(await file.text()));
    if (!rows.length) throw new Error('No object rows found in JSON');
    return { sheetNames: ['data'], sheets: { data: rowsToSheet(rows) } };
  }

  const XLSX = await loadSheetJs();
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array', cellDates: true });
  if (!wb.SheetNames.length) throw new Error('No sheets found in file');

  const sheets: Record<string, ParsedSheet> = {};
  const sheetNames: string[] = [];
  for (const sn of wb.SheetNames) {
    const ws = wb.Sheets[sn];
    if (!ws) continue;
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: null, raw: true });
    if (!rows.length) continue; // skip empty tabs
    sheets[sn] = rowsToSheet(rows);
    sheetNames.push(sn);
  }
  if (!sheetNames.length) throw new Error('No rows found in any sheet');
  return { sheetNames, sheets };
}
